import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/lib/cn';

export interface DropdownMenuItem {
  label: ReactNode;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  /** Wyroznia pozycje jako destrukcyjna (np. „Usuń turniej”). */
  destructive?: boolean;
}

export interface DropdownMenuProps {
  /** Element otwierajacy menu. Dostaje `aria-expanded` i obsluge klikniecia. */
  trigger: (props: {
    'aria-expanded': boolean;
    'aria-haspopup': 'menu';
    onClick: () => void;
    ref: React.Ref<HTMLButtonElement>;
  }) => ReactNode;
  items: readonly DropdownMenuItem[];
  /** Do ktorej krawedzi wyrownac panel. */
  align?: 'start' | 'end';
  className?: string;
}

/** Odstep panelu od przycisku otwierajacego, w pikselach. */
const MENU_GAP = 6;
/** Minimalny margines panelu od krawedzi okna. */
const VIEWPORT_MARGIN = 8;

/** Wspolrzedne panelu w ukladzie `position: fixed`. */
interface MenuPosition {
  top: number;
  left?: number;
  right?: number;
}

/**
 * Menu podreczne — „Opcje turnieju ⌄” oraz menu ⋮ na kartach kortow.
 *
 * Panel jest renderowany przez portal w `document.body` i pozycjonowany
 * `position: fixed` wzgledem przycisku. Dzieki temu nie przycinaja go
 * kontenery z przewijaniem (np. `TableContainer` z `overflow-x: auto`)
 * ani nie poszerza on ich zawartosci. Pozycja liczy sie przy otwarciu
 * i na kazdym przewinieciu/zmianie rozmiaru; przy braku miejsca pod
 * przyciskiem panel otwiera sie ku gorze.
 *
 * Zamyka sie klawiszem Escape i klinieciem poza obszarem, a po zamknieciu
 * oddaje fokus przyciskowi otwierajacemu — bez tego nawigacja klawiatura
 * gubi miejsce w dokumencie.
 */
export function DropdownMenu({ trigger, items, align = 'end', className }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Pozycja panelu — przed pierwszym rysowaniem (useLayoutEffect), zeby menu
  // nie mignelo w rogu ekranu, i dalej na scroll/resize (capture lapie takze
  // przewijanie wewnetrznych kontenerow).
  useLayoutEffect(() => {
    if (!open) return;

    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const menuHeight = menuRef.current?.offsetHeight ?? 0;
      const fitsBelow = rect.bottom + MENU_GAP + menuHeight <= window.innerHeight;
      const fitsAbove = rect.top - MENU_GAP - menuHeight >= 0;
      const top =
        !fitsBelow && fitsAbove ? rect.top - MENU_GAP - menuHeight : rect.bottom + MENU_GAP;

      const next: MenuPosition = { top };
      if (align === 'end') {
        next.right = Math.max(VIEWPORT_MARGIN, window.innerWidth - rect.right);
      } else {
        next.left = Math.max(VIEWPORT_MARGIN, rect.left);
      }
      setPosition(next);
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, align, items.length]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // Panel zyje w portalu, wiec sprawdzamy oba drzewa: przycisk i menu.
      if (!containerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={cn('relative inline-flex', className)}>
      {trigger({
        'aria-expanded': open,
        'aria-haspopup': 'menu',
        onClick: () => setOpen((current) => !current),
        ref: triggerRef,
      })}

      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            style={{
              top: position?.top ?? 0,
              ...(position?.left !== undefined ? { left: position.left } : {}),
              ...(position?.right !== undefined ? { right: position.right } : {}),
            }}
            className={cn(
              'border-border bg-surface fixed z-50 min-w-52',
              'flex flex-col gap-0.5 rounded-xl border p-1.5 shadow-lg',
              // Do czasu wyliczenia pozycji panel jest niewidoczny (bez migniecia).
              position === null && 'invisible',
            )}
          >
            {items.map((item, index) => (
              <button
                key={index}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
                className={cn(
                  'flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium',
                  'transition-colors disabled:pointer-events-none disabled:opacity-45',
                  '[&_svg]:size-4',
                  item.destructive
                    ? 'text-danger-600 hover:bg-danger-50'
                    : 'text-fg hover:bg-surface-hover',
                )}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
